"use client"

import type React from "react"
import { Label } from "./ui/label"
import { SchoolCard } from "./SchoolCard"
import { DataSourceInfo } from "./DataSourceInfo"
import CTABanner from "./CTABanner"
import { SchoolAccessProvider } from "@/contexts/SchoolAccessContext"

type SortOption =
  | "name"
  | "sv3"
  | "sv6"
  | "sv9"
  | "grades9"
  | "information_5"
  | "stimulans_5"
  | "stod_5"
  | "studiero_5"
  | "trygghet_5"
  | "information_8"
  | "stimulans_8"
  | "stod_8"
  | "studiero_8"
  | "trygghet_8"

interface School {
  skolenhetskod: string
  name: string
  kommun: string
  grades: string[]
  totalStudents: number
  studentsPerTeacher: number
  qualifiedTeachers: number | "#N/A" | string | null
  grades9: string
  profile?: string
  resourceSchool?: string
  testResults: {
    sv3: TestResult
    ma3: TestResult
    sv6: TestResult
    ma6: TestResult
    en6: TestResult
    sv9: TestResult
    ma9: TestResult
    en9: TestResult
  }
  surveyResults5: SurveyResult[]
  surveyResults8: SurveyResult[]
}

interface TestResult {
  score: number | "n/a"
  percentile: number | "n/a"
}

interface SurveyResult {
  dimension: string
  score: number
}

interface SchoolResultsProps {
  sortedSchools: School[] | undefined
  noResultsMessage: string | null
  setIsAuthModalOpen: (isOpen: boolean) => void
  sortBy: SortOption
  setSortBy: (sortBy: SortOption) => void
  sortOptions: { value: SortOption; label: string }[]
  searchParams: { [key: string]: string | string[] | undefined }
}

const SchoolResults: React.FC<SchoolResultsProps> = ({
  sortedSchools,
  noResultsMessage,
  sortBy,
  setSortBy,
  sortOptions,
  searchParams,
}) => {
  const hasSchools = sortedSchools && sortedSchools.length > 0



  return (
    <div className="mt-8 transition-all duration-300 ease-in-out">
      {(hasSchools || noResultsMessage) && (
        <div className="mt-8">
          {/* Use the CTABanner component with the click handler */}
          <CTABanner />

          <div className="flex flex-col items-start mb-4 mt-4 pl-4">
            <h2 className="text-2xl font-bold text-gray-800">Hittade skolor:</h2>
            {noResultsMessage ? (
              <p className="text-lg font-semibold text-gray-700">{noResultsMessage}</p>
            ) : (
              hasSchools && (
                <p className="text-lg font-semibold text-gray-700">{sortedSchools.length} skolor hittades</p>
              )
            )}
          </div>
          {hasSchools && (
            <>
              <div className="flex items-center mb-4 pl-4">
                <Label htmlFor="sort" className="mr-2 text-sm font-medium text-gray-700">
                  Sortera på:
                </Label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="w-[200px] h-10 rounded-md border border-gray-300 bg-white text-gray-800 px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
                {sortedSchools.map((school) => (
                  <SchoolAccessProvider
                    key={`provider-${school.skolenhetskod}`}
                    schoolId={school.skolenhetskod}
                    schoolName={school.name}
                  >
                    <SchoolCard
                      key={`${school.skolenhetskod}-${school.name}`}
                      school={school}
                      searchParams={searchParams}
                    />
                  </SchoolAccessProvider>
                ))}
              </div>
              <DataSourceInfo />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default SchoolResults

